/**
 * Native bridge helpers.
 *
 * Wraps Capacitor's Haptics + Share plugins so callers don't have to
 * care whether we're on iOS, Android or the web. Every call is safe
 * on every platform:
 *   - Web: no-op / graceful fallback.
 *   - Native: real haptic tap + native share sheet.
 *
 * The plugins are already declared in package.json and registered in
 * both `frontend/ios/App/App/capacitor.config.json` and the Android
 * platform. Dynamic imports keep them out of the web bundle so the
 * PWA payload stays tiny.
 */

import { getPlatformSync } from "../hooks/usePlatform";

let hapticsMod = null;
let shareMod = null;

async function loadHaptics() {
  if (!getPlatformSync().isNativeApp) return null;
  if (hapticsMod) return hapticsMod;
  try {
    hapticsMod = await import("@capacitor/haptics");
    return hapticsMod;
  } catch {
    return null;
  }
}

async function loadShare() {
  if (!getPlatformSync().isNativeApp) return null;
  if (shareMod) return shareMod;
  try {
    shareMod = await import("@capacitor/share");
    return shareMod;
  } catch {
    return null;
  }
}

// ─── Haptics ──────────────────────────────────────────────────────────

// A very light tap — used on secondary CTAs (toggles, small buttons).
export async function hapticLight() {
  const m = await loadHaptics();
  if (!m) return;
  try { await m.Haptics.impact({ style: m.ImpactStyle.Light }); } catch { /* ignore */ }
}

// Medium tap — used on the main CTA of a page (Book, Send, Unlock).
export async function hapticMedium() {
  const m = await loadHaptics();
  if (!m) return;
  try { await m.Haptics.impact({ style: m.ImpactStyle.Medium }); } catch { /* ignore */ }
}

// Success feedback pattern — payment success, unlock succeeded, etc.
export async function hapticSuccess() {
  const m = await loadHaptics();
  if (!m) return;
  try { await m.Haptics.notification({ type: m.NotificationType.Success }); } catch { /* ignore */ }
}

// Error feedback pattern — payment failed, network error.
export async function hapticError() {
  const m = await loadHaptics();
  if (!m) return;
  try { await m.Haptics.notification({ type: m.NotificationType.Error }); } catch { /* ignore */ }
}

// ─── Share ────────────────────────────────────────────────────────────

/**
 * Open the OS-native share sheet. Falls back to `navigator.share` on
 * mobile web (both iOS Safari and Chrome Android support it), and to
 * copy-to-clipboard on desktop.
 *
 *   openShareSheet({
 *     title: "Astro Priya Sharma",
 *     text:  "Consult with Priya on AstroKuberChat",
 *     url:   "https://astrokuberchat.com/astrologers/priya",
 *   })
 *
 * Returns `{ ok: true }` on success or `{ ok: false, error }` if the
 * user cancelled or the platform can't share. Never throws.
 */
export async function openShareSheet({ title, text, url, dialogTitle } = {}) {
  // Native plugin path — iOS + Android app shell.
  const m = await loadShare();
  if (m) {
    try {
      const can = await m.Share.canShare();
      if (can?.value !== false) {
        await m.Share.share({ title, text, url, dialogTitle: dialogTitle || title });
        return { ok: true };
      }
    } catch (e) {
      // User cancelled the native sheet — treat as no-op, not an error.
      if (String(e?.message || "").toLowerCase().includes("cancel")) return { ok: true, cancelled: true };
      return { ok: false, error: e?.message || "Share failed" };
    }
  }
  // Mobile-web fallback — iOS Safari + Chrome Android both support this.
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
    try {
      await navigator.share({ title, text, url });
      return { ok: true };
    } catch (e) {
      if (e?.name === "AbortError") return { ok: true, cancelled: true };
      // fall through to clipboard
    }
  }
  // Desktop fallback — copy the URL.
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && url) {
      await navigator.clipboard.writeText(url);
      return { ok: true, copied: true };
    }
  } catch { /* ignore */ }
  return { ok: false, error: "Sharing is not supported on this device." };
}
